import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, type RenderResponse } from '../../cloud/api';
import {
    acquireAudioFocus,
    addAudioFocusListener,
    releaseAudioFocus,
    speak,
    startListening,
    stopListening,
    stopSpeaking,
    type AudioFocusEventType,
} from '../../native/voice-native';
import { CANNED_LINES, cannedLineForError } from './voice-canned-lines';

export type VoiceSessionStatus = 'idle' | 'listening' | 'thinking' | 'speaking';
export type VoiceSpeakingKind = 'normal' | 'canned';

export interface VoiceSessionState {
    status: VoiceSessionStatus;
    speakingKind: VoiceSpeakingKind | null;
    transcript: string | null;
    responseText: string | null;
    error: string | null;
}

export interface VoiceAppendTurn {
    user: string;
    render: RenderResponse;
}

export interface UseVoiceSessionDeps {
    startListening?: () => Promise<string>;
    stopListening?: () => Promise<void>;
    speak?: (text: string) => Promise<void>;
    stopSpeaking?: () => Promise<void>;
    acquireAudioFocus?: () => Promise<void>;
    releaseAudioFocus?: () => Promise<void>;
    renderVoice?: (query: string) => Promise<RenderResponse>;
    appendTurn?: (turn: VoiceAppendTurn) => void;
    addAudioFocusListener?: (
        listener: (event: AudioFocusEventType) => void,
    ) => Promise<{ remove(): void | Promise<void> }>;
}

export interface UseVoiceSessionResult {
    state: VoiceSessionState;
    transcript: string | null;
    responseText: string | null;
    error: string | null;
    start: () => Promise<void>;
    cancel: (reason?: string) => Promise<void>;
    interrupt: (reason: string) => Promise<void>;
}

const IDLE_STATE: VoiceSessionState = {
    status: 'idle',
    speakingKind: null,
    transcript: null,
    responseText: null,
    error: null,
};

async function defaultRenderVoice(query: string): Promise<RenderResponse> {
    return api.render({
        trigger: 'direct_query',
        query,
        voice: true,
    });
}

export function useVoiceSession(deps?: UseVoiceSessionDeps): UseVoiceSessionResult {
    const resolved = useMemo(() => ({
        startListening: deps?.startListening ?? startListening,
        stopListening: deps?.stopListening ?? stopListening,
        speak: deps?.speak ?? speak,
        stopSpeaking: deps?.stopSpeaking ?? stopSpeaking,
        acquireAudioFocus: deps?.acquireAudioFocus ?? acquireAudioFocus,
        releaseAudioFocus: deps?.releaseAudioFocus ?? releaseAudioFocus,
        renderVoice: deps?.renderVoice ?? defaultRenderVoice,
        appendTurn: deps?.appendTurn ?? (() => {}),
        addAudioFocusListener: deps?.addAudioFocusListener ?? addAudioFocusListener,
    }), [deps]);

    const [state, setState] = useState<VoiceSessionState>(IDLE_STATE);
    const stateRef = useRef(state);
    const runIdRef = useRef(0);
    const focusHeldRef = useRef(false);

    useEffect(() => { stateRef.current = state; }, [state]);

    const releaseFocusOnce = useCallback(async () => {
        if (!focusHeldRef.current) return;
        focusHeldRef.current = false;
        await resolved.releaseAudioFocus();
    }, [resolved]);

    const terminate = useCallback(async (reason: string) => {
        runIdRef.current += 1;
        await Promise.all([
            resolved.stopListening().catch(() => {}),
            resolved.stopSpeaking().catch(() => {}),
        ]);
        await releaseFocusOnce();
        setState({ ...IDLE_STATE, error: reason });
    }, [releaseFocusOnce, resolved]);

    const cancel = useCallback(async (reason = 'cancelled') => {
        await terminate(reason);
    }, [terminate]);

    const interrupt = useCallback(async (reason: string) => {
        await terminate(reason || 'interrupted');
    }, [terminate]);

    useEffect(() => {
        let disposed = false;
        let remove: (() => void | Promise<void>) | null = null;
        void resolved.addAudioFocusListener((event) => {
            if (disposed) return;
            if (event === 'audioFocusLost') void interrupt('audio_focus_lost');
        }).then((handle) => {
            remove = handle.remove.bind(handle);
            if (disposed) void remove();
        });
        return () => {
            disposed = true;
            if (remove) void remove();
        };
    }, [interrupt, resolved]);

    const start = useCallback(async () => {
        if (stateRef.current.status !== 'idle') {
            await terminate('barge_in');
        }

        const runId = runIdRef.current + 1;
        runIdRef.current = runId;
        focusHeldRef.current = false;
        let transcript: string | null = null;
        let render: RenderResponse | null = null;

        const isCurrent = () => runIdRef.current === runId;

        try {
            setState({
                status: 'listening',
                speakingKind: null,
                transcript: null,
                responseText: null,
                error: null,
            });
            await resolved.acquireAudioFocus();
            if (!isCurrent()) return;
            focusHeldRef.current = true;

            transcript = await resolved.startListening();
            if (!isCurrent()) return;
            setState({
                status: 'thinking',
                speakingKind: null,
                transcript,
                responseText: null,
                error: null,
            });

            try {
                render = await resolved.renderVoice(transcript);
            } catch {
                throw new Error('render_error');
            }
            const responseText = render.text.trim();
            if (!responseText) throw new Error('render_empty');
            render = { ...render, text: responseText };
            if (!isCurrent()) return;
            setState({
                status: 'speaking',
                speakingKind: 'normal',
                transcript,
                responseText,
                error: null,
            });

            try {
                await resolved.speak(responseText);
            } catch {
                throw new Error('tts_error');
            }
            if (!isCurrent()) return;
            resolved.appendTurn({ user: transcript, render });
            setState({
                status: 'idle',
                speakingKind: null,
                transcript,
                responseText,
                error: null,
            });
        } catch (err) {
            if (!isCurrent()) return;
            const kind = cannedLineForError(err);
            const line = CANNED_LINES[kind];
            if (kind === 'road') {
                setState({
                    status: 'idle',
                    speakingKind: null,
                    transcript,
                    responseText: line,
                    error: err instanceof Error ? err.message : String(err),
                });
                return;
            }
            setState({
                status: 'speaking',
                speakingKind: 'canned',
                transcript,
                responseText: line,
                error: err instanceof Error ? err.message : String(err),
            });
            await resolved.speak(line).catch(() => {});
            if (!isCurrent()) return;
            setState({
                status: 'idle',
                speakingKind: null,
                transcript,
                responseText: line,
                error: err instanceof Error ? err.message : String(err),
            });
        } finally {
            if (isCurrent()) await releaseFocusOnce();
        }
    }, [releaseFocusOnce, resolved, terminate]);

    useEffect(() => () => {
        runIdRef.current += 1;
        void resolved.stopListening().catch(() => {});
        void resolved.stopSpeaking().catch(() => {});
        void releaseFocusOnce();
    }, [releaseFocusOnce, resolved]);

    return {
        state,
        transcript: state.transcript,
        responseText: state.responseText,
        error: state.error,
        start,
        cancel,
        interrupt,
    };
}
